/**
 * Custom hook for managing file upload state and operations
 */

import { useState, useCallback } from 'react';
import { parseCSV, parseXLSX, parseThroughputCSV, parseThroughputXLSX, processRoadmapFile, parseLeadTimeBreakdownCSV, parseLeadTimeBreakdownXLSX, parseBloqueiosCSV, parseBloqueiosXLSX } from '../utils/parsers';

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB

export function useFileUpload() {
  const [fileName, setFileName] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const [internalMessage, setInternalMessage] = useState('');
  const [internalMessageType, setInternalMessageType] = useState('');
  const [uploadKey, setUploadKey] = useState(0);

  // Reset upload state
  const resetUpload = useCallback(() => {
    setInternalMessage('');
    setInternalMessageType('');
    setError('');
    setFileName('');
    setUploadKey(key => key + 1);
  }, []);

  // Process file for lead time analysis
  const processLeadTimeFile = useCallback(async (file) => {
    if (file.size > MAX_FILE_SIZE) {
      throw new Error('O arquivo é muito grande. O limite é 2MB.');
    }
    try {
      let processedData;
      if (file.type === 'text/csv') {
        processedData = await parseCSV(file);
      } else if (file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || file.name.endsWith('.xlsx')) {
        processedData = await parseXLSX(file);
      } else {
        throw new Error('Formato de arquivo não suportado. Envie um arquivo CSV ou XLSX.');
      }
      return processedData;
    } catch (err) {
      throw err; // Re-throw para que a função chamadora possa tratar
    }
  }, []);

  // Process file for throughput analysis
  const processThroughputFile = useCallback(async (file) => {
    if (file.size > MAX_FILE_SIZE) {
      throw new Error('O arquivo é muito grande. O limite é 2MB.');
    }
    try {
      let processedData;
      if (file.type === 'text/csv') {
        processedData = await parseThroughputCSV(file);
      } else if (file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || file.name.endsWith('.xlsx')) {
        processedData = await parseThroughputXLSX(file);
      } else {
        throw new Error('Formato de arquivo não suportado. Envie um arquivo CSV ou XLSX.');
      }
      return processedData;
    } catch (err) {
      throw err; // Re-throw para que a função chamadora possa tratar
    }
  }, []);

  // Process file for lead time breakdown analysis
  const processLeadTimeBreakdownFile = useCallback(async (file) => {
    if (file.size > MAX_FILE_SIZE) {
      throw new Error('O arquivo é muito grande. O limite é 2MB.');
    }
    try {
      let processedData;
      if (file.type === 'text/csv') {
        processedData = await parseLeadTimeBreakdownCSV(file);
      } else if (file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || file.name.endsWith('.xlsx')) {
        processedData = await parseLeadTimeBreakdownXLSX(file);
      } else {
        throw new Error('Formato de arquivo não suportado. Envie um arquivo CSV ou XLSX.');
      }
      return processedData;
    } catch (err) {
      throw err; // Re-throw para que a função chamadora possa tratar
    }
  }, []);

  // Process file for bloqueios analysis
  const processBloqueiosFile = useCallback(async (file) => {
    console.log('🎯 processBloqueiosFile - Iniciando processamento');
    if (file.size > MAX_FILE_SIZE) {
      throw new Error('O arquivo é muito grande. O limite é 2MB.');
    }
    try {
      let processedData;
      if (file.type === 'text/csv') {
        console.log('📄 processBloqueiosFile - Processando CSV');
        processedData = await parseBloqueiosCSV(file);
      } else if (file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || file.name.endsWith('.xlsx')) {
        console.log('📊 processBloqueiosFile - Processando XLSX');
        processedData = await parseBloqueiosXLSX(file);
      } else {
        throw new Error('Formato de arquivo não suportado. Envie um arquivo CSV ou XLSX.');
      }
      console.log('✅ processBloqueiosFile - Processamento concluído:', processedData?.length, 'itens');
      return processedData;
    } catch (err) {
      console.error('❌ processBloqueiosFile - Erro:', err);
      throw err; // Re-throw para que a função chamadora possa tratar
    }
  }, []);

  // Handle file upload from input
  const handleFileUpload = useCallback(async (event, chartType, onDataProcessed, leadTimeType = null) => {
    const file = event.target.files[0];
    
    // Clear previous state
    setInternalMessage('');
    setInternalMessageType('');
    setError('');
    setUploadKey(key => key + 1);
    
    if (!file) return;
    
    if (file.size > MAX_FILE_SIZE) {
      setInternalMessage("Arquivo excede o limite de 2MB.");
      setInternalMessageType("error");
      return;
    }
    
    const isCSV = file.type === 'text/csv' || file.name.endsWith('.csv');
    const isXLSX = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || file.name.endsWith('.xlsx');
    
    if (!isCSV && !isXLSX) {
      setInternalMessage("Formato inválido. Envie um arquivo .csv ou .xlsx.");
      setInternalMessageType("error");
      return;
    }
    
    setIsProcessing(true);
    try {
      let processedData;
      if (chartType === 'roadmap') {
        // Validação e processamento específico para roadmap
        processedData = await processRoadmapFile(file);
        setFileName(file.name);
      } else if (chartType === 'leadtime' && leadTimeType === 'breakdown') {
        // Validação e processamento específico para lead time breakdown
        processedData = await processLeadTimeBreakdownFile(file);
        setFileName(file.name);
      } else if (chartType === 'leadtime') {
        // Validação e processamento específico para lead time tradicional
        processedData = await processLeadTimeFile(file);
        setFileName(file.name);
      } else if (chartType === 'throughput') {
        // Validação e processamento específico para throughput
        processedData = await processThroughputFile(file);
        setFileName(file.name);
      } else if (chartType === 'bloqueios') {
        // Validação e processamento específico para bloqueios
        console.log('🎯 useFileUpload - Processando arquivo de bloqueios');
        processedData = await processBloqueiosFile(file);
        console.log('✅ useFileUpload - Dados processados:', processedData?.length, 'itens');
        setFileName(file.name);
      } else {
        // Fallback para compatibilidade (caso chartType não seja específico)
        processedData = await processLeadTimeFile(file);
        setFileName(file.name);
      }
      
      if (processedData) {
        console.log('🚀 useFileUpload - Chamando onDataProcessed com:', processedData?.length, 'itens');
        setInternalMessage("Arquivo válido! Pronto para visualização.");
        setInternalMessageType("success");
        onDataProcessed(processedData);
      }
      setIsProcessing(false);
    } catch (err) {
      setInternalMessage(err.message || "Erro ao validar o arquivo. Tente novamente.");
      setInternalMessageType("error");
      setIsProcessing(false);
    }
  }, [processLeadTimeFile, processThroughputFile, processLeadTimeBreakdownFile, processBloqueiosFile]);

  // Get example file path based on chart type and lead time type
  const getExamplePath = useCallback((type, leadTimeType = null) => {
    const paths = {
      roadmap: '/examples/roadmap/example.xlsx',
      leadtime: leadTimeType === 'breakdown' 
        ? '/examples/leadtime/example-leadtime-breakdown.xlsx'
        : '/examples/leadtime/example.xlsx',
      throughput: '/examples/throughput/example.xlsx',
      bloqueios: '/examples/bloqueios/example.xlsx'
    };
    return paths[type];
  }, []);

  // Load example file
  const loadExampleFile = useCallback(async (chartType, onDataProcessed, leadTimeType = null) => {
    console.log('🎯 useFileUpload - loadExampleFile chamado:', { chartType, leadTimeType });
    setIsProcessing(true);
    setError("");
    try {
      const examplePath = getExamplePath(chartType, leadTimeType);
      console.log('📁 useFileUpload - Caminho do exemplo:', examplePath);
      if (!examplePath) {
        throw new Error(`Tipo de gráfico inválido: ${chartType}`);
      }

      let response = await fetch(examplePath);
      if (!response.ok) {
        throw new Error(`Arquivo de exemplo não encontrado: ${examplePath}`);
      }

      const arrayBuffer = await response.arrayBuffer();
      const file = new File([arrayBuffer], 'example.xlsx', { 
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" 
      });
      
      console.log('⚙️ useFileUpload - Processando arquivo de exemplo para:', chartType);
      const processedData = chartType === 'roadmap' 
        ? await processRoadmapFile(file)
        : chartType === 'leadtime' && leadTimeType === 'breakdown'
        ? await processLeadTimeBreakdownFile(file)
        : chartType === 'leadtime'
        ? await processLeadTimeFile(file)
        : chartType === 'throughput'
        ? await processThroughputFile(file)
        : chartType === 'bloqueios'
        ? await processBloqueiosFile(file)
        : await processLeadTimeFile(file); // fallback
      
      console.log('✅ useFileUpload - Dados do exemplo processados:', processedData?.length, 'itens');
      
      if (!processedData) {
        throw new Error("Erro ao processar o arquivo de exemplo");
      }
      
      setFileName('example.xlsx');
      console.log('🚀 useFileUpload - Chamando onDataProcessed (exemplo) com:', processedData?.length, 'itens');
      onDataProcessed(processedData);
    } catch (err) {
      setError(err.message || "Erro ao carregar o arquivo de exemplo.");
    } finally {
      setIsProcessing(false);
    }
  }, [processLeadTimeFile, processThroughputFile, getExamplePath]);

  // Download example file
  const downloadExampleFile = useCallback(async (chartType, leadTimeType = null) => {
    try {
      const examplePath = getExamplePath(chartType, leadTimeType);
      if (!examplePath) {
        throw new Error(`Tipo de gráfico inválido: ${chartType}`);
      }

      const response = await fetch(examplePath);
      if (!response.ok) {
        throw new Error(`Arquivo de exemplo não encontrado: ${examplePath}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      let downloadName = 'example.xlsx';
      if (leadTimeType === 'breakdown') {
        downloadName = 'example-leadtime-breakdown.xlsx';
      } else if (chartType === 'bloqueios') {
        downloadName = 'example-bloqueios.xlsx';
      }
      
      a.download = downloadName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError(err.message || "Erro ao baixar o arquivo de exemplo.");
    }
  }, [getExamplePath]);

  // Set message and message type
  const setMessage = useCallback((msg) => {
    setInternalMessage(msg);
  }, []);

  const setMessageType = useCallback((type) => {
    setInternalMessageType(type);
  }, []);

  // Set message with type in one call
  const showMessage = useCallback((msg, type = 'info') => {
    setInternalMessage(msg);
    setInternalMessageType(type);
  }, []);

  return {
    // States
    fileName,
    isProcessing,
    error,
    message: internalMessage,
    messageType: internalMessageType,
    uploadKey,
    
    // Functions
    handleFileUpload,
    loadExampleFile,
    downloadExampleFile,
    resetUpload,
    showMessage,
    setMessage,
    setMessageType,
    
    // Internal functions (for direct use if needed)
    processLeadTimeFile,
    processThroughputFile
  };
}
